import { useQuery } from "@apollo/client";
import {
  parseFilterModelFromSearchParams,
  parseSortModel,
  filterModelToSearchParams,
  serializeSortModel,
} from "common/graphql/queryString";
import Paper from "@mui/material/Paper";
import {
  DataGridPro,
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import { useApolloClient } from "@apollo/client";
import { DAILY_TRANSACTION_AMOUNT_COLUMN } from "../common";
import {
  CurbsuiteTransactionLogEntry,
  GatewayTransactionType,
  GET_CURBSUITE_TRANSACTIONS,
  GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
  GetCurbsuiteTransactionsData,
  GetCurbsuiteTransactionsFilter,
  GetCurbsuiteTransactionsVars,
  TransactionOrderBy,
} from "common/graphql/queries/GetCurbsuiteTransactions";
import formatDate from "date-fns/format";
import parseDate from "date-fns/parse";
import PayeeTableToolbar from "../PayeeTableToolbar";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import type { Theme } from "@mui/material/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

export const CurbPassTransactionsTable: React.FC<CurbPassTransactionsTableProps> = ({
  variant,
  onClickTransaction,
}) => {
  const client = useApolloClient();

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: "transactionIndex+desc",
  });

  const [now] = React.useState(new Date());
  const selectedDate = new Date(searchParams.get("date") || startOfMonth(now));
  const setSelectedDate = (newDate: Date) => {
    searchParams.set("date", newDate.toISOString());
    setSearchParams(searchParams, { replace: true });
  };

  const selectedEpoch = selectedDate.getTime();
  const [since, until] = React.useMemo(() => [startOfMonth(selectedEpoch), endOfMonth(selectedEpoch)], [selectedEpoch]);

  const TABLE_COLUMNS = React.useMemo(
    () => [
      {
        field: "transactionIndex",
        headerName: "ID",
        type: "string",
        valueGetter({ row }) {
          return row.transaction.index ? row.transaction.index.toString().padStart(6, "0") : null;
        },
        sortingOrder: ["desc", "asc"],
        filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
        filterable: true,
      } as GridColDef<CurbsuiteTransactionLogEntry, string>,
      {
        field: "transactionDate",
        headerName: "Date",
        type: "date",
        valueGetter({ row }) {
          return parseDate(row.transactionDate, "yyyy-MM-dd", new Date());
        },
        valueFormatter: (params) => formatDate(params.value, "MM/dd/yyyy"),
        filterable: true,
        filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
      } as GridColDef<CurbsuiteTransactionLogEntry, Date, string>,
      ...(variant === "park"
        ? [
            {
              field: "timeRange",
              headerName: "Park Period",
              sortComparator: (v1, v2) => v1.end.getTime() - v2.end.getTime(),
              flex: 1,
              align: "left",
              headerAlign: "left",
              sortingOrder: ["desc", "asc"],
              valueGetter({ row }) {
                return {
                  start: new Date(row.startTime || 0),
                  end: new Date(row.endTime || 0),
                };
              },
              valueFormatter: (params) =>
                `${formatDate(params.value.start, PARK_PERIOD_FORMAT)} - ${formatDate(
                  params.value.end,
                  PARK_PERIOD_FORMAT,
                )}`,
              width: 200,
              filterable: false,
            } as GridColDef<CurbsuiteTransactionLogEntry, { start: Date; end: Date }, string>,
            {
              field: "licensePlate",
              headerName: "License Plate",
              align: "left",
              headerAlign: "left",
              type: "string",
              sortable: false,
              flex: 1,
              valueGetter({ row }) {
                return `${row.transaction.registeredVehicle?.stateCode} ${row.transaction.registeredVehicle?.licensePlate}`;
              },
              filterable: true,
              filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
            } as GridColDef<CurbsuiteTransactionLogEntry, string, string>,
            {
              field: "addressStreet",
              headerName: "Address",
              align: "left",
              headerAlign: "left",
              type: "string",
              sortable: false,
              valueGetter({ row }) {
                return row.addressStreet;
              },
              width: 240,
              filterable: false,
            } as GridColDef<CurbsuiteTransactionLogEntry, string>,
          ]
        : [
            {
              field: "transactionType",
              headerName: "Transaction Type",
              type: "string",
              flex: 1,
              valueGetter({ row }) {
                return row.transaction.gatewayTransactions[0].gatewayTransactionType;
              },
              valueFormatter: (params) => {
                switch (params.value) {
                  case "wallet_reload":
                    return "Wallet Reload";
                  case "initial_wallet_load":
                    return "New Account";
                  case "wallet_refund":
                    return "Wallet Refund";
                  default:
                    return "Unknown";
                }
              },
              filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
              filterable: true,
            } as GridColDef<CurbsuiteTransactionLogEntry, string>,
          ]),
      DAILY_TRANSACTION_AMOUNT_COLUMN,
      ...(variant === "park"
        ? [
            {
              field: "actions",
              type: "actions",
              sortable: false,
              flex: 1,
              getActions: (params) => [
                <GridActionsCellItem
                  label={"View Transaction"}
                  icon={<ArrowForwardIosIcon sx={{ color: (theme: Theme) => theme.palette.action.active }} />}
                  onClick={() => onClickTransaction(params.row.transactionId)}
                />,
              ],
            } as GridActionsColDef,
          ]
        : []),
    ],
    [variant, onClickTransaction],
  );

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;

    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const PAGE_SIZE = 1000;

  const sort = searchParams.get("sort") || "transactionIndex+desc";
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  const orderBy: TransactionOrderBy[] = React.useMemo(() => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "transactionIndex":
        return [{ created_at: sortDirection || "desc" }];
      case "timeRange":
        return [{ end_time: sortDirection || "desc" }];
      default:
        return [{ created_at: "desc" }];
    }
  }, [sortModel]);

  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      // Manually apply filters when user clears all filters
      handleApplyFilters(newFilterModel);
    }

    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  const filter: GetCurbsuiteTransactionsFilter = React.useMemo(() => {
    const baseFilter = {
      transaction_type: { _eq: variant === "park" ? ("park" as const) : ("gateway_transaction" as const) },
    };

    if (!appliedFilters) {
      return baseFilter;
    }

    const columnFilters = appliedFilters.items
      .map((item) => {
        if (item.columnField === "transactionIndex") {
          return {
            transaction: {
              index: { _eq: item.value },
            },
          };
        }
        if (item.columnField === "transactionType") {
          return {
            transaction: {
              gateway_transactions: {
                gateway_transaction_type: { _eq: item.value as GatewayTransactionType },
              },
            },
          };
        }

        if (item.columnField === "transactionDate") {
          switch (item.operatorValue) {
            case "is":
              return {
                transaction_date: { _eq: item.value },
              };
            case "before":
              return {
                transaction_date: { _lt: item.value },
              };
            case "after":
              return {
                transaction_date: { _gt: item.value },
              };
          }
        }

        if (item.columnField === "licensePlate") {
          return {
            transaction: {
              registered_vehicle: { license_plate: { _eq: item.value.toUpperCase() } },
            },
          };
        }

        return undefined;
      })
      .filter(Boolean);

    if (appliedFilters.linkOperator === "and") {
      return { ...baseFilter, _and: columnFilters };
    } else {
      return { ...baseFilter, _or: columnFilters };
    }
  }, [variant, appliedFilters]);

  const { data, loading, error } = useQuery<GetCurbsuiteTransactionsData, GetCurbsuiteTransactionsVars>(
    GET_CURBSUITE_TRANSACTIONS,
    {
      variables: {
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        orderBy,
        filter,
        since,
        until,
        transactionType: variant === "park" ? "park" : "gateway_transaction",
        priceFilter: GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
      },
      fetchPolicy: "network-only",
    },
  );

  // Functions for custom export
  const fetchTotalCount = async () => {
    const result = await client.query<GetCurbsuiteTransactionsData, GetCurbsuiteTransactionsVars>({
      query: GET_CURBSUITE_TRANSACTIONS,
      fetchPolicy: "network-only",
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter,
        since,
        until,
        transactionType: variant === "park" ? "park" : "gateway_transaction",
        priceFilter: GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
      },
    });
    return result.data?.aggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetCurbsuiteTransactionsData, GetCurbsuiteTransactionsVars>({
      query: GET_CURBSUITE_TRANSACTIONS,
      fetchPolicy: "network-only",
      variables: {
        offset,
        limit,
        orderBy,
        filter,
        since,
        until,
        transactionType: variant === "park" ? "park" : "gateway_transaction",
        priceFilter: GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
      },
    });
    return result.data?.transactions || [];
  };

  return (
    <Paper sx={{ borderRadius: 2, maxWidth: 1084, height: 876 }}>
      <DataGridPro
        components={{
          Toolbar: PayeeTableToolbar,
          FilterPanel: ManualApplyGridFilterPanel,
        }}
        componentsProps={{
          filterPanel: {
            onApplyFilters: handleApplyFilters,
          },
          toolbar: {
            fetchTotalCount,
            fetchDataBatch,
            exportFileName: `curbpass_${variant}_${Date.now()}`,
            columns: TABLE_COLUMNS,
            selectedDate,
            onChangeSelectedDate: setSelectedDate,
            maxDate: now,
            batchSize: PAGE_SIZE,
          },
        }}
        getRowId={(row) => row.transactionId}
        columns={TABLE_COLUMNS}
        rows={data?.transactions || []}
        rowCount={data?.aggregate.aggregate.count || 0}
        loading={loading}
        disableMultipleColumnsSorting
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        error={error}
        pagination
        page={page - 1}
        onPageChange={handlePageChange}
        pageSize={PAGE_SIZE}
        paginationMode={"server"}
        rowsPerPageOptions={[PAGE_SIZE]}
        sortingMode={"server"}
        sortModel={sortModel}
        filterMode={"server"}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        initialState={{
          sorting: {
            sortModel: [{ field: "transactionIndex", sort: "desc" }],
          },
        }}
        onSortModelChange={handleSortModelChange}
        sx={{
          ".MuiDataGrid-columnHeaderTitle": {
            fontSize: "18px",
            fontWeight: 700,
            lineHeight: "24px",
            letterSpacing: 0.17,
          },
        }}
      />
    </Paper>
  );
};

export type CurbPassTransactionsTableVariant = "wallet" | "park";

export interface CurbPassTransactionsTableProps {
  variant: CurbPassTransactionsTableVariant;
  onClickTransaction: (transactionId: string) => void;
}

const PARK_PERIOD_FORMAT = "hh:mm aa";

export default CurbPassTransactionsTable;
